import { omit } from "lodash"
import moment from "moment"
import i18n from "../i18n"
import { IAnswer, IQuestion } from "../types/types"

const keyQuestions = [
  "principal_consultation_reason",
  "symptoms_beginning",
  "principal_cervical_consultation_reason"
]

export const validatePwd = (pwd: string) => {
  if (pwd) {
    if (
      !pwd.match(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[●!"#$%&'()*+,\-.\/:;<=>?@[\\\]^_`{|}~])[\s\S]{8,32}$/
      )
    )
      return i18n.t("validation.password_requirements")
  }
  return undefined
}

export const validateBirthday = (date: string) => {
  if (date) {
    if (date.length === 10) {
      const todayDate = moment()
      const birthday = moment(date, "YYYY-MM-DD", true)

      if (birthday.isValid()) {
        if (birthday > todayDate) {
          return i18n.t("validation.birthday_in_past")
        } else if (todayDate.diff(birthday, "years", true) < 18) {
          return i18n.t("validation.minimum_age")
        } else if (todayDate.diff(birthday, "years", true) > 100) {
          return i18n.t("validation.invalid_birthday")
        }
      } else {
        return i18n.t("validation.invalid_date_format")
      }
    } else return i18n.t("validation.invalid_date_format")
  }

  return undefined
}

export const validateWeight = (value: string) => {
  const weight = parseInt(value)
  if (weight < 0) return i18n.t("validation.positive_number")
  if (weight > 500) return i18n.t("validation.weight_max")
  return undefined
}

export const validateHeight = (value: string) => {
  const height = parseInt(value)
  if (height < 0) return i18n.t("validation.positive_number")
  if (height > 300) return i18n.t("validation.height_max")
  return undefined
}

export const validatePhoneNumber = (value: string) => {
  if (!validatePhoneNumberValue(value))
    return i18n.t("validation.invalid_phone")
  return undefined
}

const validatePhoneNumberValue = (value: string) => {
  const regex = /^(0|\+33)[1-9]([-.: ]?[0-9]{2}){4}$/
  if (!regex.test(value)) {
    return false
  } else {
    return true
  }
}

export const formatAPIPhone = (value: string) => {
  if (!value) return ""

  return value.replace(/\s/g, "")
}

export const validateConsultationDate = (date: string) => {
  if (date) {
    if (date.length === 10) {
      const todayDate = moment()
      const consultationDate = moment(date, "YYYY-MM-DD", true)

      if (consultationDate.isValid()) {
        if (consultationDate < todayDate) {
          return i18n.t("validation.consultation_date_future")
        }
      } else return i18n.t("validation.invalid_date_format")
    }
  }

  return undefined
}

export const isAnswerChecked = (
  questionId: number,
  answers: Array<IAnswer>,
  value: number
) => {
  const _ans = answers?.find((answer) => answer.question_id === questionId)
  return _ans ? value === _ans.value : false
}

export const isQuestionKey = (question: IQuestion) =>
  keyQuestions.includes(question.question_reference)

type ConsistentQuestionsAndAnswers = {
  questions: Array<IQuestion> | undefined
  answers: Array<IAnswer> | undefined
}

const getDefaultValueForQuestion = (question: IQuestion) => {
  const dependentQuestion = dependentQuestions.find(
    (_q) => _q.question_reference === question.question_reference
  )
  return dependentQuestion?.defaultValue
}

export const checkQuestionsConsistency = (
  questions: Array<IQuestion> | undefined,
  answers: Array<IAnswer> | undefined
): ConsistentQuestionsAndAnswers => {
  const _questions = questions?.map((_q) => {
    return {
      ..._q,
      disabled: determineQuestionDisabled(_q, questions, answers)
    }
  })

  const _answers = _questions?.map((_q) => {
    const ans = answers?.find((_ans) => _ans.question_id === _q.id)
    return _q.disabled
      ? {
          question_id: _q.id,
          value: getDefaultValueForQuestion(_q)
        }
      : ans
        ? ans
        : undefined
  })

  return {
    questions: _questions
      ?.filter((_q) => !_q.disabled)
      .map((_q) => omit(_q, "disabled")),
    answers: _answers?.filter((_ans): _ans is IAnswer => _ans !== undefined)
  }
}

const dependentQuestions = [
  {
    question_reference: "filter_consultation_date",
    defaultValue: "1970-01-01",
    dependency: {
      question_reference: "filter_consultation_date_existence",
      values: [0]
    }
  },
  {
    question_reference: "filter_first_consultation_same_motive",
    defaultValue: 0,
    dependency: {
      question_reference: "filter_first_consultation",
      values: [1]
    }
  },
  {
    question_reference: "filter_muscular_weakness_comment",
    defaultValue: "-",
    dependency: {
      question_reference: "filter_muscular_weakness",
      values: [0]
    }
  },
  {
    question_reference: "urinal_problems_comment",
    defaultValue: "-",
    dependency: {
      question_reference: "urinal_problems",
      values: [0]
    }
  },
  {
    question_reference: "lumbar_pain_fracture_link",
    defaultValue: 0,
    dependency: {
      question_reference: "principal_consultation_reason",
      values: [1, 2, 4, 5, 6]
    }
  },
  {
    question_reference: "lumbar_pain_fall_link",
    defaultValue: 0,
    dependency: {
      question_reference: "principal_consultation_reason",
      values: [1, 2, 4, 5, 6]
    }
  },
  {
    question_reference: "filter_upper_body_muscular_weakness_comment",
    defaultValue: "-",
    dependency: {
      question_reference: "filter_upper_body_muscular_weakness",
      values: [0]
    }
  }
]

const determineQuestionDisabled = (
  question: IQuestion,
  questions: Array<IQuestion>,
  answers: Array<IAnswer> | undefined
) => {
  const dependentQuestion = dependentQuestions.find(
    (dq) => dq.question_reference === question.question_reference
  )
  if (dependentQuestion) {
    const determiningQuestion = questions.find(
      (_q) =>
        dependentQuestion.dependency.question_reference ===
        _q.question_reference
    )
    if (determiningQuestion) {
      const answer = answers?.find(
        (ans) => ans.question_id === determiningQuestion.id
      )

      return answer
        ? dependentQuestion.dependency.values.includes(
            typeof answer.value === "string"
              ? parseInt(answer.value)
              : typeof answer.value === "number"
                ? answer.value
                : 0
          )
        : true
    }
  }
  return false
}
